import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Container, Paper, useMediaQuery, useTheme } from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
    AuthLibMSALTokenAcquisitionError,
    useMSALPayload
} from "@initia-digital/auth-client-utils";
import { baseDefinedAndNotNullNarrowing, BasicLogger } from "@initia-digital/ts-utils";
import { BottomSearchAppBar, TextButton, useSnackbar } from "@initia-digital/component-library";
import { IPrestart } from "@initia-digital/types/work-and-safety";

import { useWorkAndSafetyStore } from "../../../../../../store";
import { putPrestart } from "../../../../../../api/mutate";
import { BasicAppBarPage } from "../../../../../../layouts/BasicAppBarPage";
import { useJobSearch, usePrestartDetail } from "../../../../../../hooks";
import { EmergencyDirectiveBanner } from "../../../../../../legacy/EmergencyDirectiveBanner";
import { PrestartHeader } from "../../../../../../legacy/PrestartHeader";
import { PrestartStart } from "../../../../../../legacy/PrestartStart";
import { ReviewThePlans } from "../../../../../../legacy/ReviewThePlans";
import { HazardObservations } from "../../../../../../legacy/HazardObservations";
import { ClosureAndContacts } from "../../../../../../legacy/ClosureAndContacts";
import { WorkingWithOthers } from "../../../../../../legacy/WorkingWithOthers";
import { PrestartActions } from "../../../../../../legacy/PrestartActions";

const logger = new BasicLogger({ logLevel: import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 2, logFormat: 'BROWSER' });


const EditPrestartDetail = () => {
    // -------------------------------------------------
    const {
        editPrestartState, updateEditPrestartState
    } = useWorkAndSafetyStore();
    const {
        id, status, leadingTeam
    } = editPrestartState;

    const navigate = useNavigate();
    const { jobId, planId, prestartId } = useParams();

    // MUI HOOKS
    // ---------------------
    const theme = useTheme();
    const isSmallerThanMd = useMediaQuery(theme.breakpoints.down('md'));
    const { queueSnackbar } = useSnackbar();

    // AUTH AND DATA INTEGRATION
    // ---------------------
    const authDetail = useMSALPayload();

    const searchJob = useJobSearch(authDetail);

    const queryClient = useQueryClient();

    // PRESTART DETAIL
    const { prestartDetailData, prestartDetailLoading } = usePrestartDetail(authDetail, prestartId!, baseDefinedAndNotNullNarrowing(prestartId));

    // MUTATIONS
    // ---------------------
    const updatePrestart = useMutation({
        mutationFn: putPrestart,
        onSuccess: (prestartUpdate) => {
            // ---------------------           
            updateEditPrestartState({
                ...prestartUpdate
            });
            queryClient.invalidateQueries({ queryKey: ['prestart', prestartId!] });

            queueSnackbar(`Prestart saved`, 'success', 'Success', 3000, '1rem');
        },
        onError: (error) => {
            // ----------------------------
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
                || ['Unauthorized', 'Could not get fresh ID token'].includes(error.message)
            ) {
                // --------------------------------------------
                queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'error', 'Unauthorised');
                const { msal } = authDetail;
                msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                });
            }
            else if (error instanceof Error) {
                logger.error('Error saving prestart', { error });
            }
            logger.error('Error saving prestart');
            queueSnackbar('Prestart could not be saved', 'error', 'Error saving prestart', 3000, '1rem');
        }
    });

    // EFFECTS
    // Initialize edit prestart state from prestart detail
    // ---------------------
    useEffect(() => {
        // When you land on the edit-prestart page, you want to load the prestart detail fresh in all instances
        // 1. The jobId or planId has changed (i.e. this is a different plan to what is already held in state)
        if (
            baseDefinedAndNotNullNarrowing(jobId) && baseDefinedAndNotNullNarrowing(planId)
            && prestartDetailData && !prestartDetailLoading
        ) {
            // ---------------------
            updateEditPrestartState({
                ...prestartDetailData
            });
        }
    }, [prestartDetailData, jobId, planId, prestartId, prestartDetailLoading, updateEditPrestartState]);


    // EVENT HANDLERS
    // ---------------------
    const handleSave = async (directUpdate?: IPrestart) => {
        // ----------------------------------------------
        if (!baseDefinedAndNotNullNarrowing(prestartId)) {
            throw new Error('Prestart ID could not be picked up');
        }
        if (!baseDefinedAndNotNullNarrowing(jobId)) {
            throw new Error('Job ID could not be picked up');
        }
        if (!baseDefinedAndNotNullNarrowing(planId)) {
            throw new Error('Plan ID could not be picked up');
        }

        // DEFAULT behaviour is to use the editPlanState
        let prestartUpdate = editPrestartState;
        if (baseDefinedAndNotNullNarrowing(directUpdate)) {
            // -------------------------------------
            prestartUpdate = directUpdate;
        }

        await updatePrestart.mutateAsync({
            prestartId,
            prestart: {
                ...prestartUpdate,
                jobId,
                planId
            },
            authDetail
        });
    }


    return (
        <BasicAppBarPage bgColor={"white"}>
            <Container maxWidth="md" sx={{ px: !isSmallerThanMd ? "25px" : "0px", py: "10px" }}>
                <EmergencyDirectiveBanner />
                <Box mx="10px" my="5px" pb="65px" height="100%">
                    <Paper sx={{ height: '100%', width: '100%' }} elevation={0}>
                        <Box
                            display="flex" flexDirection="row" justifyContent="center"
                            pt={{ xs: '10px', sm: '15px', md: '20px' }}
                            px={{ xs: '5px', md: '20px' }}
                            height="100%"
                        >
                            <Box display="flex" flexDirection="column" sx={{ width: '100%' }} mb="20px">
                                <Box display="flex" flexDirection="column">
                                    <Box>
                                        <Box display="flex" mb="10px">
                                            <TextButton
                                                color={theme.palette.primary.contrastText}
                                                hoverBackgroundColor={theme.palette.primary.main}
                                                onClick={() => {
                                                    navigate(`/job/${jobId}`)
                                                }}
                                                startIcon={<ArrowBackIcon />}
                                            >
                                                {`Back to ${jobId}`}
                                            </TextButton>
                                        </Box>
                                    </Box>
                                    <PrestartHeader
                                        id={id}
                                        status={status}
                                        leadingTeam={leadingTeam}
                                    />
                                </Box>
                                <Box
                                    display="flex" flexDirection="column"
                                    mt="10px"
                                >
                                    <PrestartStart />
                                </Box>
                                <Box
                                    display="flex" flexDirection="column"
                                    mt="10px"
                                >
                                    <ReviewThePlans
                                        handleSave={handleSave}
                                    />
                                </Box>
                                <Box
                                    display="flex" flexDirection="column"
                                    mt="10px"
                                >
                                    <HazardObservations
                                        handleSave={handleSave}
                                    />
                                </Box>
                                <Box
                                    display="flex" flexDirection="column"
                                    mt="10px"
                                >
                                    <WorkingWithOthers />
                                </Box>
                                <Box
                                    display="flex" flexDirection="column"
                                    mt="10px"
                                >
                                    <ClosureAndContacts
                                        handleSave={handleSave}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
                <PrestartActions
                    handleSave={handleSave}
                />
                <BottomSearchAppBar
                    placeholder='Search for a job'
                    label='Search for a Job'
                    labelKey='jobId'
                    secondaryKeysForDisplay={['jobId']}
                    suggestionKeysForOptionDisplay={['jobId', 'jobName']}
                    suggestionKeysToSearchOption={['jobId', 'jobName']}
                    uniqueOptionKeyProps={['jobId']}
                    handleChange={(value) => { navigate(`/job/${value.jobId}`) }}
                    searchSuggestion={searchJob}
                />
            </Container>
        </BasicAppBarPage>
    )
}

export default EditPrestartDetail;